import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { ONE, TEN, ZERO } from "@/constants/constants";

export type SortDirection = "asc" | "desc";

interface SortState {
  field?: string;
  direction?: SortDirection;
}

export interface PaginationMeta {
  limit?: number;
  offset?: number;
  currentPage?: number;
  totalPages?: number;
  totalResult?: number;
  sortBy?: string;
  orderBy?: SortDirection;
  search?: string;
}

export interface ResponsePagination {
  limit: number;
  currentPage: number;
  offset: number;
  startIndex: number;
  endIndex: number;
  totalPages: number;
  totalResult: number;
  handleNextClick: () => void;
  handlePreviousClick: () => void;
  handlePageUpdate: (currentPage: number) => void;
  handleLimitUpdate: (limit: number) => void;
  handleOffsetUpdate: (offset: number) => void;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
  resetPagination: () => void;
  resetSorting: () => void;
  sortBy?: string;
  orderBy?: SortDirection;
  handleSort: (field: string) => void;
  isSorted: (field: string) => boolean;
  getSortDirection?: (field: string) => SortDirection | undefined;
}

export const usePagination = (
  paginationData: PaginationMeta,
  defaultLimit = TEN,
  setPaginationData?: React.Dispatch<React.SetStateAction<PaginationMeta>>,
): ResponsePagination => {
  const [_searchParam, setSearchParam] = useSearchParams();
  const limitExists = _searchParam.has("limit");

  const [limit, setLimit] = useState(defaultLimit);
  const [currentPage, setCurrentPage] = useState(ONE);
  const [offset, setOffset] = useState(ZERO);
  const [totalPages, setTotalPages] = useState(ONE);
  const [totalResult, setTotalResult] = useState(ZERO);

  const [sort, setSort] = useState<SortState>({
    field: _searchParam.get("sortBy") || undefined,
    direction: (_searchParam.get("orderBy") as SortDirection) || undefined,
  });

  const handlePageUpdate = (_page: number): void => {
    if (currentPage === _page) {
      return;
    }
    setCurrentPage(_page);
    if (setPaginationData) {
      setPaginationData((d) => {
        return { ...d, currentPage: _page, offset: limit * (_page - ONE) };
      });
    }
  };

  const handleLimitUpdate = (_limit: number): void => {
    if (limit === _limit) {
      return;
    }

    setLimit(_limit);
    if (setPaginationData) {
      setPaginationData((d) => {
        return { ...d, limit: _limit };
      });
    }
  };

  const handleOffsetUpdate = (_offset: number): void => {
    if (offset === _offset) {
      return;
    }
    setOffset(_offset);
    if (setPaginationData) {
      setPaginationData((d) => {
        return { ...d, offset: _offset };
      });
    }
  };

  const handleTotalPagesUpdate = (_totalPages: number): void => {
    if (totalPages === _totalPages) {
      return;
    }
    setTotalPages(_totalPages);
    if (setPaginationData) {
      setPaginationData((d) => {
        return {
          ...d,
          totalPages: _totalPages,
        };
      });
    }
  };

  const handleTotalResultsUpdate = (_totalResult: number): void => {
    if (totalResult === _totalResult) {
      return;
    }
    setTotalResult(_totalResult);
    if (setPaginationData) {
      setPaginationData((d) => {
        return { ...d, totalResult: _totalResult };
      });
    }
  };

  const handleNextClick = (): void => {
    // check validation for the last page
    setCurrentPage(currentPage + ONE);
    if (setPaginationData) {
      setPaginationData((d) => {
        return { ...d, currentPage: currentPage + ONE };
      });
    }
  };

  const handlePreviousClick = (): void => {
    // check validation for the first page
    setCurrentPage(currentPage - ONE);
    if (setPaginationData) {
      setPaginationData((d) => {
        return { ...d, currentPage: currentPage - ONE };
      });
    }
  };

  const updateSortState = (newSort: SortState): void => {
    setSort(newSort);

    if (setPaginationData) {
      setPaginationData((d) => ({
        ...d,
        orderBy: newSort?.direction,
        sortBy: newSort?.field,
      }));
    } else {
      // Update URL params
      const params = new URLSearchParams(_searchParam);

      if (newSort.field) {
        params.set("sortBy", newSort.field);
        params.set("orderBy", newSort.direction || "desc");
      } else {
        params.delete("sortBy");
        params.delete("orderBy");
      }

      setSearchParam(params);
    }
  };

  // Determines the next sort state based on current state and clicked field
  const getNextSortState = (field: string): SortState => {
    // If clicking a different field, start with descending
    if (sort.field !== field) {
      return { field, direction: "desc" };
    }

    // Cycle through sort states for the same field
    switch (sort.direction) {
      case "desc":
        return { field, direction: "asc" };
      case "asc":
        return { field: undefined, direction: undefined };
      default:
        return { field, direction: "desc" };
    }
  };

  const handleSort = (field: string): void => {
    const newSort = getNextSortState(field);
    updateSortState(newSort);
  };

  useEffect(() => {
    if (setPaginationData || limitExists) {
      return;
    }

    setSearchParam((prev) => {
      const params = new URLSearchParams(prev);
      if (!params.has("limit")) {
        params.set("limit", defaultLimit.toString());
      }
      if (!params.has("offset")) {
        params.set("offset", "0");
      }
      if (!params.has("currentPage")) {
        params.set("currentPage", "1");
      }

      return params;
    });
  }, [defaultLimit, limitExists, setSearchParam]);

  const resetSorting = (): void => {
    updateSortState({ field: undefined, direction: undefined });
  };

  const resetPagination = (): void => {
    handlePageUpdate(ONE);
    handleLimitUpdate(defaultLimit ?? TEN);
    resetSorting();
  };

  useEffect(() => {
    if (paginationData) {
      handleLimitUpdate(paginationData?.limit ?? limit);
      handlePageUpdate(paginationData?.currentPage ?? currentPage);
      handleOffsetUpdate(paginationData?.offset ?? offset);
      handleTotalPagesUpdate(paginationData?.totalPages ?? totalPages);
      handleTotalResultsUpdate(paginationData?.totalResult ?? totalResult);
    }
  }, [paginationData]);

  useEffect(() => {
    if (
      limit !== paginationData?.limit ||
      currentPage !== paginationData?.currentPage ||
      offset !== paginationData?.offset ||
      !limitExists
    ) {
      if (setPaginationData) {
        return;
      }

      setSearchParam((prev) => {
        const params = new URLSearchParams(prev);
        params.set("limit", limit.toString());
        params.set("offset", (limit * (currentPage - ONE)).toString());
        params.set("currentPage", currentPage.toString());

        return params;
      });
    }
  }, [limit, currentPage, offset]);

  return useMemo(() => {
    const startIndex = offset + ONE;
    const endIndex = Math.min(offset + limit, totalResult);
    const hasPreviousPage = currentPage > ONE;
    const hasNextPage = currentPage < totalPages;

    return {
      limit,
      currentPage,
      offset: limit * currentPage,
      startIndex,
      endIndex,
      totalPages,
      totalResult,
      handleNextClick,
      handlePreviousClick,
      handlePageUpdate,
      handleLimitUpdate,
      handleOffsetUpdate,
      hasPreviousPage,
      hasNextPage,
      resetPagination,
      resetSorting,
      handleSort,
      sortBy: sort.field,
      orderBy: sort.direction,
      isSorted: (field: string) => sort.field === field,
      getSortDirection: (field: string) =>
        sort.field === field ? sort.direction : undefined,
    };
  }, [limit, offset, currentPage, totalPages, sort]);
};
